import { z } from 'zod'

import { ensureRouteData } from '@/app/query-client'
import { heatmapQueryOptions } from '@/features/heatmap/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { heatSizingSchema } from '@/schemas/heatmap'

/**
 * Both the depth and the sizing live in the URL. Moved here from the screen
 * file, as `stockSearchSchema` moved into `StockScreen.loader.ts`: the loader
 * below parses `location.search` through this same schema, and the route's
 * `validateSearch` imports it back so the two can never disagree about which
 * map a URL means.
 */
export const heatmapSearchSchema = z.object({
  sector: z.string().min(1).optional().catch(undefined),
  sizing: heatSizingSchema.default('cap').catch('cap'),
})

/**
 * The `/market/heatmap` loader.
 *
 * MAIN, keyed on the URL's `sector`/`sizing` — awaited for the ENTRY URL
 * only, per the "Search params" rule: there is no `loaderDeps`, so pressing a
 * tile (a `sector` change) or the sizing chip re-runs no loader at all — the
 * owner's "must not shift on click" ruling depends on exactly that. The
 * screen keeps reading through the plain `useHeatmap` (`placeholderData:
 * keepPreviousData`, unchanged by this conversion): a drill-in dims the last
 * map in place instead of suspending the route to a full-page skeleton.
 *
 * No secondary reads: `heatmapQueryOptions` already existed as a factory
 * (`features/heatmap/api/queries.ts`) before this wave, so nothing there
 * changed either.
 */
export async function loadHeatmap({
  context: { queryClient },
  location,
}: {
  context: RouterContext
  location: { search: unknown }
}): Promise<void> {
  const { sector, sizing } = heatmapSearchSchema.parse(location.search)
  await ensureRouteData(
    queryClient,
    heatmapQueryOptions({ sizing, ...(sector === undefined ? {} : { sector }) }),
  )
}
